import { useId, type ReactNode } from "react";
import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { DownloadIcon, InfoIcon } from "../../icons";
import { formatReportCell } from "../../utils/reportCells";
import { downloadCsv, reportTableToCsv } from "../../utils/reportCsv";
import type { ReportTableData } from "../../types/reports";

function InfoTip({ title, className = "" }: { title: string; className?: string }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <span className={`inline-flex cursor-help ${className}`}>
        <InfoIcon className="size-3.5 text-gray-400" />
      </span>
    </Tooltip>
  );
}

export default function ReportTable({ title, table, csvFilename, toolbar }: { title: string; table: ReportTableData; csvFilename: string; toolbar?: ReactNode }) {
  const titleId = useId();

  return (
    <Box component="section" aria-labelledby={titleId}>
      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
        <Typography id={titleId} variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>{title}</Typography>
        {table.notes.length > 0 && <InfoTip title={table.notes.join(" ")} />}
        <IconButton size="small" aria-label={`Export ${title} as CSV`} onClick={() => downloadCsv(csvFilename, reportTableToCsv(table))}>
          <DownloadIcon className="size-4" />
        </IconButton>
      </Box>
      {toolbar && <Box mb={1}>{toolbar}</Box>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-labelledby={titleId} sx={{ "& th, & td": { whiteSpace: "nowrap", px: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>{table.rowLabel}</TableCell>
              {table.columns.map((c) => (
                <TableCell key={c.key} align="right">
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.map((row) => (
              <TableRow key={row.label} sx={row.isTotal ? { "& td, & th": { fontWeight: 600 } } : undefined}>
                <TableCell component="th" scope="row">{row.label}</TableCell>
                {table.columns.map((c) => (
                  <TableCell key={c.key} align="right">
                    {row.cellNotes?.[c.key] && <InfoTip title={row.cellNotes[c.key]} className="mr-1 align-middle" />}
                    {formatReportCell(c.format, row.cells[c.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
